import { Container, Graphics } from 'pixi.js';
import { rnd } from './util';

export interface InfallParticle { a: number; r: number; om: number; tail: number; kind: 'orbit' | 'stream'; }
export interface HotPatch { a: number; r: number; om: number; phase: number; life: number; life0: number; }
export interface CaptureFlash { x: number; y: number; t: number; }
export interface BlackHole {
  x: number; y: number; r: number; t: number; consumed: number;
  view: Graphics; diskView: Graphics; infallView: Graphics;
  life: number; persistent: boolean;
  infall: InfallParticle[]; spawnAcc: number;
  patches: HotPatch[]; flashes: CaptureFlash[];
}
export interface WarpPair { a: WarpHole; b: WarpHole; life: number; }
export interface WarpHole { x: number; y: number; r: number; color: number; cool: Map<string, number>; view: Graphics; }

export class HazardSystem {
  blackHoles: BlackHole[] = [];
  bhT = 0;
  warps: WarpPair[] = [];
  warpT = 0;
  layer: Container;

  constructor(stage: Container) {
    this.layer = new Container();
    stage.addChild(this.layer);
  }

  spawnBlackHole(W: number, H: number, atCenter = false): void {
    const mass      = atCenter ? 1.0 : 0.55 + Math.random() * 1.35; // 0.55 → 1.90
    const SHADOW_R = 64;
    const PHOT_R   = 70;

    // diskView — empty (disk removed)
    const diskView = new Graphics();
    this.layer.addChild(diskView);

    // Shadow + outer halo + photon ring
    const v = new Graphics();
    v.circle(0, 0, SHADOW_R * 2.2);
    v.fill({ color: 0x000000, alpha: 0.10 });
    v.circle(0, 0, SHADOW_R * 1.4);
    v.fill({ color: 0x000000, alpha: 0.20 });
    v.circle(0, 0, PHOT_R);
    v.stroke({ width: 2.5, color: 0xffeedd, alpha: 0.70 });
    v.circle(0, 0, PHOT_R + 3);
    v.stroke({ width: 1, color: 0x88aaff, alpha: 0.28 });
    v.circle(0, 0, SHADOW_R);
    v.fill({ color: 0x000000 });
    v.scale.set(mass);
    this.layer.addChild(v);

    const infallView = new Graphics();
    this.layer.addChild(infallView);

    const bh: BlackHole = {
      x: atCenter ? W / 2 : rnd(W * 0.25, W * 0.75),
      y: atCenter ? H / 2 : rnd(H * 0.3, H * 0.7),
      r: SHADOW_R * mass, t: 0, consumed: 0, view: v, diskView, infallView,
      life: atCenter ? Infinity : 10 + 8 * mass,
      persistent: atCenter,
      infall: [], spawnAcc: 0,
      patches: [], flashes: [],
    };
    v.x = bh.x; v.y = bh.y;
    diskView.x = bh.x; diskView.y = bh.y;
    infallView.x = bh.x; infallView.y = bh.y;
    this.blackHoles.push(bh);
  }

  spawnWarpPair(W: number, H: number): void {
    const drawWarp = (color: number): Graphics => {
      const v = new Graphics();
      // Outer halo — bigger overall so the hitbox visually matches
      v.circle(0, 0, 60);
      v.fill({ color, alpha: 0.08 });
      v.circle(0, 0, 48);
      v.fill({ color, alpha: 0.18 });
      // Body
      v.circle(0, 0, 38);
      v.fill({ color, alpha: 0.45 });
      v.stroke({ width: 3, color, alpha: 0.95 });
      // Inner ring
      v.circle(0, 0, 22);
      v.stroke({ width: 2, color: 0xffffff, alpha: 0.7 });
      // White-hot core
      v.circle(0, 0, 10);
      v.fill({ color: 0xffffff, alpha: 0.85 });
      return v;
    };
    const va = drawWarp(0x00ffcc);
    this.layer.addChild(va);
    const vb = drawWarp(0xff44ff);
    this.layer.addChild(vb);
    const a: WarpHole = { x: rnd(60, W - 60), y: rnd(60, H - 60), r: 38, color: 0x00ffcc, cool: new Map(), view: va };
    const b: WarpHole = { x: rnd(60, W - 60), y: rnd(60, H - 60), r: 38, color: 0xff44ff, cool: new Map(), view: vb };
    va.x = a.x; va.y = a.y;
    vb.x = b.x; vb.y = b.y;
    this.warps.push({ a, b, life: 10 });
  }

  update(dt: number, W: number, H: number, isBoss: boolean, waveBreak: boolean): void {
    if (!isBoss && !waveBreak) {
      this.warpT += dt;
      if (this.warps.length === 0 && this.warpT > rnd(8, 12)) {
        this.warpT = 0;
        this.spawnWarpPair(W, H);
      }
      this.bhT += dt;
      // 1 BH at a time, 45-75s between spawns
      if (this.blackHoles.length === 0 && this.bhT > rnd(45, 75)) {
        this.bhT = 0;
        this.spawnBlackHole(W, H, false);
      }
    }

    for (let i = this.blackHoles.length - 1; i >= 0; i--) {
      const bh = this.blackHoles[i];
      bh.t += dt;
      // Lifespan tick — random-spawn black holes expire; persistent (boss arena) ones live forever
      if (!bh.persistent) {
        bh.life -= dt;
        const a = Math.min(1, bh.life / 1.5);
        bh.view.alpha = a;
        bh.diskView.alpha = a;
        bh.infallView.alpha = a;
        if (bh.life <= 0) {
          bh.view.parent?.removeChild(bh.view);
          bh.view.destroy();
          bh.diskView.parent?.removeChild(bh.diskView);
          bh.diskView.destroy();
          bh.infallView.parent?.removeChild(bh.infallView);
          bh.infallView.destroy();
          this.blackHoles.splice(i, 1);
        }
      }
    }

    for (let i = this.warps.length - 1; i >= 0; i--) {
      const w = this.warps[i];
      w.life -= dt;
      if (w.life <= 0) {
        w.a.view.parent?.removeChild(w.a.view);
        w.b.view.parent?.removeChild(w.b.view);
        w.a.view.destroy();
        w.b.view.destroy();
        this.warps.splice(i, 1);
      }
    }
  }

  clearAll(): void {
    for (const bh of this.blackHoles) {
      bh.view.parent?.removeChild(bh.view);       bh.view.destroy();
      bh.diskView.parent?.removeChild(bh.diskView); bh.diskView.destroy();
      bh.infallView.parent?.removeChild(bh.infallView); bh.infallView.destroy();
    }
    this.blackHoles.length = 0;
    this.bhT = 0;
    for (const w of this.warps) {
      w.a.view.parent?.removeChild(w.a.view); w.a.view.destroy();
      w.b.view.parent?.removeChild(w.b.view); w.b.view.destroy();
    }
    this.warps.length = 0;
    this.warpT = 0;
  }

  pullVelocity(obj: { x: number; y: number; vx: number; vy: number }, dt: number, strength = 1, noTangential = false): void {
    for (const bh of this.blackHoles) {
      const dx = bh.x - obj.x, dy = bh.y - obj.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < 8000 * 8000) {
        const d = Math.sqrt(d2) || 1;
        const massFactor = bh.r / 64;
        const closeT = Math.max(0, 1 - d / (100 * massFactor));
        const pull = ((12000000 + 48000000 * closeT * closeT) / (d2 + 80)) * strength * massFactor;
        obj.vx += (dx / d) * pull * dt;
        obj.vy += (dy / d) * pull * dt;
        if (!noTangential) {
          obj.vx += (-dy / d) * pull * 0.55 * dt;
          obj.vy += (dx / d) * pull * 0.55 * dt;
        }
      }
    }
  }

  // Local time-dilation factor near a black hole. Returns 1.0 when far,
  // dropping to 0.15 when the player is hugging the event horizon. Applied
  // to enemies + ebullets + particles + the inner hazards tick in main.ts.
  bhTimeScale(px: number, py: number): number {
    let scale = 1;
    for (const bh of this.blackHoles) {
      const d = Math.hypot(bh.x - px, bh.y - py);
      if (d >= 420) continue;
      const t = Math.max(0, Math.min(1, 1 - (d - 60) / 360));   // 0 at d=420, 1 at d=60
      const s = 1 - 0.85 * t;                                    // 1.0 → 0.15
      if (s < scale) scale = s;
    }
    return scale;
  }

  // Direction + magnitude from player toward nearest BH (for the chromatic warp overlay
  // in main.ts). Returns null when no BH is in influence range.
  nearestBhInfluence(px: number, py: number): { dx: number; dy: number; intensity: number } | null {
    let best: { dx: number; dy: number; intensity: number } | null = null;
    for (const bh of this.blackHoles) {
      const dx = bh.x - px, dy = bh.y - py;
      const d = Math.hypot(dx, dy);
      if (d >= 420) continue;
      const intensity = Math.max(0, Math.min(1, 1 - (d - 60) / 360));
      if (!best || intensity > best.intensity) {
        best = { dx: dx / Math.max(1, d), dy: dy / Math.max(1, d), intensity };
      }
    }
    return best;
  }

  consumes(obj: { x: number; y: number }): boolean {
    for (const bh of this.blackHoles) {
      const dx = bh.x - obj.x, dy = bh.y - obj.y;
      if (dx * dx + dy * dy < (bh.r * 0.55) ** 2) {
        bh.consumed += 1;
        return true;
      }
    }
    return false;
  }

  // Warp teleport: returns true if teleported.
  // Exit velocity (when obj has vx/vy) gets a random angle so warp feels chaotic
  // — speed magnitude preserved, direction is randomized full 360°.
  warp(obj: { x: number; y: number; vx?: number; vy?: number }, id: string): boolean {
    const now = Date.now();
    for (const w of this.warps) {
      const tryWarp = (from: WarpHole, to: WarpHole): boolean => {
        const dx = from.x - obj.x, dy = from.y - obj.y;
        // Generous hitbox — visual r=24, collision r=42 (1.75× radius) so warps are easy to fly into
        if (dx * dx + dy * dy < (from.r * 1.75) ** 2) {
          if ((from.cool.get(id) ?? 0) > now) return false;
          obj.x = to.x; obj.y = to.y;
          if (typeof obj.vx === 'number' && typeof obj.vy === 'number') {
            const sp = Math.hypot(obj.vx, obj.vy);
            // Preserve speed but pick a random exit angle
            const ang = Math.random() * Math.PI * 2;
            // Exit at 2× entry speed so warp feels like a slingshot
            const exitSp = Math.max(sp * 2, 440);
            obj.vx = Math.cos(ang) * exitSp;
            obj.vy = Math.sin(ang) * exitSp;
          }
          to.cool.set(id, now + 800);
          return true;
        }
        return false;
      };
      if (tryWarp(w.a, w.b)) return true;
      if (tryWarp(w.b, w.a)) return true;
    }
    return false;
  }

  clearForWaveBreak(): void {
    for (const bh of this.blackHoles) {
      bh.view.parent?.removeChild(bh.view);
      bh.view.destroy();
      bh.infallView.parent?.removeChild(bh.infallView);
      bh.infallView.destroy();
    }
    this.blackHoles.length = 0;
    for (const w of this.warps) {
      w.a.view.parent?.removeChild(w.a.view);
      w.b.view.parent?.removeChild(w.b.view);
      w.a.view.destroy();
      w.b.view.destroy();
    }
    this.warps.length = 0;
  }
}
